import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { FaArrowRight, FaPassport, FaUniversity, FaFileSignature, FaUserPlus, FaUpload, FaComments, FaPlaneDeparture, FaGraduationCap } from "react-icons/fa";
import "../styles/EducationSupport.css";
import heroImg from "../assets/education-support.png";
import LogoMarquee from "../components/LogoMarquee";

export default function EducationSupport() {

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const slideUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
  };

  return (
    <div className="eduPage">

      {/* 1. HERO SECTION */}
      <section className="eduHero">
        <motion.div
          className="eduHeroContent"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <motion.div variants={slideUp} className="eduBadge">Global Education</motion.div>
          <motion.h1 variants={slideUp}>Your Gateway to <br />Global Education</motion.h1>
          <motion.p variants={slideUp}>
            Simplifying the path to international education for students worldwide.
            Trusted, secure, and dedicated to your success. Get complete visa and
            academic support from start to finish.
          </motion.p>

          <motion.div variants={slideUp} className="eduActions">
            <button
              className="eduBtnPrimary"
              onClick={() => window.open("https://rambvisa.smartomegalk.com/", "_blank")}
            >
              Access Visa Portal <FaArrowRight />
            </button>
            <button
              className="eduBtnOutline"
              onClick={() => window.scrollBy({ top: window.innerHeight * 0.8, behavior: 'smooth' })}
            >
              Learn More
            </button>
          </motion.div>
        </motion.div>

        <motion.div
          className="eduHeroImg"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <img src={heroImg} alt="Education Support" />
          <div className="eduImgOverlay" />
        </motion.div>
      </section>

      {/* 2. SERVICES: EVERYTHING YOU NEED */}
      <motion.div
        className="eduSectionHeader"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={slideUp}
      >
        <h2>Everything You Need for Success</h2>
        <p>Comprehensive consultancy and documentation services tailored to ensure a smooth transition abroad.</p>
      </motion.div>

      <motion.section
        className="eduSuccessGrid"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
        variants={staggerContainer}
      >
        <motion.div className="eduFeatureCard" variants={slideUp}>
          <div className="eduIconWrap"><FaPassport /></div>
          <h3>Visa Consultation</h3>
          <p>Expert guidance through every step of the visa application process, tailored to your destination.</p>
          <ul className="eduFeatureList">
            <li>One-on-one expert consultation</li>
            <li>Profile & eligibility assessment</li>
            <li>Mock interview preparation</li>
            <li>Post-arrival support</li>
          </ul>
        </motion.div>

        <motion.div className="eduFeatureCard" variants={slideUp}>
          <div className="eduIconWrap"><FaUniversity /></div>
          <h3>University Selection</h3>
          <p>Choose from top-ranked universities worldwide that perfectly match your academic goals and budget.</p>
          <ul className="eduFeatureList">
            <li>Access to 50+ partner universities</li>
            <li>Course mapping by career goals</li>
            <li>Scholarship assistance</li>
          </ul>
        </motion.div>

        <motion.div className="eduFeatureCard" variants={slideUp}>
          <div className="eduIconWrap"><FaFileSignature /></div>
          <h3>Document Support</h3>
          <p>Full assistance with preparing, verifying, and submitting all necessary documentation securely.</p>
          <ul className="eduFeatureList">
            <li>Secure digital upload portal</li>
            <li>Translation & attestation guidance</li>
            <li>SOP & Resume editing services</li>
          </ul>
        </motion.div>
      </motion.section>

      {/* 3. HOW IT WORKS TIMELINE */}
      <div className="eduTimelineSection">
        <motion.div
          className="eduSectionHeader"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={slideUp}
        >
          <h2>How It Works</h2>
          <p>4 simple steps to kickstart your journey to studying abroad.</p>
        </motion.div>

        <motion.div
          className="eduTimeline"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.div className="eduStep" variants={slideUp}>
            <div className="eduStepNum"><FaUserPlus /></div>
            <h4>1. Register Online</h4>
            <p>Create your student account in minutes and get your unique RAMB ID.</p>
          </motion.div>
          <motion.div className="eduStep" variants={slideUp}>
            <div className="eduStepNum"><FaUpload /></div>
            <h4>2. Upload Profile</h4>
            <p>Complete your profile and upload academic documents securely.</p>
          </motion.div>
          <motion.div className="eduStep" variants={slideUp}>
            <div className="eduStepNum"><FaComments /></div>
            <h4>3. Connect with Expert</h4>
            <p>Our agents will review everything and guide you through the process.</p>
          </motion.div>
          <motion.div className="eduStep" variants={slideUp}>
            <div className="eduStepNum"><FaPlaneDeparture /></div>
            <h4>4. Fly High</h4>
            <p>Get your visa approved, pack your bags, and start your journey abroad.</p>
          </motion.div>
        </motion.div>
      </div>

      {/* 4. FINANCIAL SERVICES / EDUCATION LOAN */}
      <section className="eduAdditional">
        <motion.div
          className="eduLoanCard"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="eduIconWrap"><FaGraduationCap /></div>
          <h3>Need Financial Support?</h3>
          <p>
            Secure your academic future without financial worries. Our education loan options offer
            competitive interest rates and flexible repayment to ensure your dreams are fully funded.
          </p>
          <button className="eduBtnPrimary" onClick={() => window.location.href = '/loans'}>
            View Education Loans
          </button>
        </motion.div>
      </section>

      {/* 5. MARQUEE */}
      <LogoMarquee speed={30} />

    </div>
  );
}
